/**
 * SO surface formatting helpers (S2.2).
 */

const amountFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** 2dp money display (grouping per user locale, digits stable). */
export function formatAmount(value: number): string {
  return amountFormatter.format(value);
}

/**
 * Which hold-reason string a surface should use (2026-09-16).
 *
 * On a SAVED order the floor quoted is the one snapshotted when it was priced
 * — the company figure may since have changed or been cleared, so the saved
 * voice says "the minimum this order was priced against". Before the save the
 * floor IS today's company minimum, so the pre-save voice names it as such.
 */
export function holdReasonKey(reason: 'deviation' | 'low_margin', saved: boolean): string {
  if (reason !== 'low_margin') return 'orders:so.holdReason.deviation';
  return saved ? 'orders:so.holdReason.low_marginSaved' : 'orders:so.holdReason.low_margin';
}

/**
 * A percentage figure (margin, markup, minimum margin) at 2dp, no unit — the
 * ONE formatter every floor interpolation uses, so the same floor never
 * reads "30" on one surface and "30.00" on the next. '' when absent.
 */
export function formatPct(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : amountFormatter.format(value);
}

/** "AUD 1,234.50" money-with-currency display. */
export function formatMoney(currency: string, value: number): string {
  return `${currency} ${formatAmount(value)}`;
}

/**
 * Discount-badge precision for the 售價矩陣預覽 (2026-08-28): whole number
 * when the DERIVED percentage sits within a tenth of one (5.01 → "5", the
 * policy an operator recognises), otherwise one decimal. Presentation only —
 * the underlying `discountPct` keeps its full derived value, and the exact
 * money is the cell above the badge. Lives here, not in the tsx, because the
 * S5.1 engine-purity gate rightly forbids arithmetic on price-ish operands
 * inside the section component.
 */
export function formatDiscountBadge(pct: number): string {
  const whole = Math.round(pct);
  return Math.abs(pct - whole) < 0.1 ? String(whole) : pct.toFixed(1);
}

const quantityFormatter = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
});

/**
 * Quantity display — locale thousands grouping, no forced decimals ("1,250",
 * "12.5"). The house rule for a raw count column (ItemLocationsSection's
 * NumericCell renders quantities the same way); a bare `{qty}` printed
 * "1250" and broke the numeric column's scan-ability at four digits.
 */
export function formatQuantity(value: number): string {
  return quantityFormatter.format(value);
}
